/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  CSSProperties,
  MutableRefObject,
  useState,
  useEffect,
  useRef,
} from "react";
import { When, If, Then } from "react-if";
import { VariableSizeList as List } from "react-window";
import {
  Checkbox,
  FlexContainer,
  Text,
  Loader,
  theme,
  IndeterminateCheckbox,
} from "@nordcloud/gnui";
import { FilterHeader, ExpandIcon } from "~/components";
import { TextButton } from "~/components/Styled";
import { noop, trimText, isNil } from "~/tools";
import { Separator, SidebarItemWrapperGrid } from "../Styled";
import { useSelectableListFilter, useResize } from "./hooks";
import { SwitchWrap } from "./styles";
import { Item, Mode } from "./types";
import { getSelectedMode } from "./utils";

// label heigh + sublabel heigh  + padding top + padding bottom
const ROW_HEIGHT_WITH_SUBLABEL = 24 + 18 + 8 + 8;
// label heigh  + padding top + padding bottom
const ROW_HEIGHT = 24 + 8 + 8;
// 5 rows
const MAX_LIST_HEIGHT = 200;

type Props = {
  allowModeChange?: boolean;
  isInfiniteScroll?: boolean;
  elementRef?: MutableRefObject<null>;
  isLoading?: boolean;
  selectableItems: Item[];
  selectedOptions: string[];
  selectedSubOptions?: string[];
  isExcludeSelected?: boolean;
  onMultiSelect: (ids: string[]) => void;
  onMultiSubSelect?: (ids: string[]) => void;
  onModeChange?: (mode: Mode) => void;
};

export function SelectableListWithSearch({
  allowModeChange = false,
  isInfiniteScroll = false,
  isLoading = false,
  elementRef,
  selectedOptions = [],
  selectedSubOptions = [],
  selectableItems,
  isExcludeSelected = false,
  onMultiSelect,
  onMultiSubSelect,
  onModeChange = noop,
}: Props) {
  const {
    selectedItems,
    selectedSubItems,
    filteredOptions,
    areAllFilteredOptionsSelected,
    setSearch,
    onApply,
    onSubItemApply,
    selectAllFilteredOptions,
    deselectAllFilteredOptions,
  } = useSelectableListFilter({
    selectableItems,
    selectedOptions,
    selectedSubOptions,
    onMultiSelect,
    onMultiSubSelect,
  });
  const filterHeaderRef =
    useRef<HTMLDivElement>() as MutableRefObject<HTMLDivElement | null>;
  const [selectableListWidth, setSelectableListWidth] = useState(0);

  const listRef = useRef<List | null>(null);

  useEffect(() => {
    setSelectableListWidth(filterHeaderRef?.current?.offsetWidth ?? 0);
  }, [filterHeaderRef, setSelectableListWidth]);

  useResize(filterHeaderRef, setSelectableListWidth);

  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const toggleRow = (index: number) => {
    setExpanded((prev) => {
      const next = { ...prev, [index]: !prev[index] };

      // Tell react-window to recalc sizes
      listRef?.current?.resetAfterIndex(index, false);
      return next;
    });
  };

  // eslint-disable-next-line react/function-component-definition
  const SubList = ({
    index,
    style,
  }: {
    index: number;
    style?: CSSProperties;
  }) => {
    const { value, subCheckboxes } = filteredOptions[index];

    return (
      <>
        {subCheckboxes?.map((row) => {
          return (
            <div key={row.value} style={style}>
              <SidebarItemWrapperGrid
                key={row.value}
                hideBottomBorder
                title={row.label ?? ""}
                onClick={() =>
                  onSubItemApply(row.value ?? "", value ?? "", subCheckboxes)
                }
              >
                <div style={{ width: "24px" }} />
                <div style={{ width: "16px" }} />
                <SwitchWrap>
                  <Checkbox
                    withoutLabel
                    readOnly
                    checked={isChecked(selectedSubItems, row.value ?? "")}
                  />
                </SwitchWrap>
                <FlexContainer direction="column" alignItems="start">
                  <Text align="left" mb={0} color={theme.color.text.text01}>
                    {isNil(row.label) ? row.label : trimText(row.label, 35)}
                  </Text>
                  <If condition={row.subLabel}>
                    <Then>
                      <Text
                        align="left"
                        mb={0}
                        size="sm"
                        color={theme.color.text.text01}
                      >
                        {row.subLabel}
                      </Text>
                    </Then>
                  </If>
                </FlexContainer>
              </SidebarItemWrapperGrid>
            </div>
          );
        })}
      </>
    );
  };

  // eslint-disable-next-line react/function-component-definition
  const ExpandableRow = ({
    index,
    style,
  }: {
    index: number;
    style: CSSProperties;
  }) => {
    const { label, subLabel, value, subCheckboxes } = filteredOptions[index];
    return (
      <div style={style}>
        <SidebarItemWrapperGrid
          key={value}
          hideBottomBorder
          title={label ?? ""}
          onClick={() => {
            if (subCheckboxes) {
              toggleRow(index);
              onApply(value ?? "", subCheckboxes);
            } else {
              onApply(value ?? "", []);
            }
          }}
        >
          <When condition={!!subCheckboxes}>
            <ExpandIcon
              noMargin
              name="chevronDown"
              title="chevronDown"
              isExpanded={expanded[index]}
              onClick={(event) => {
                toggleRow(index);
                event.stopPropagation();
              }}
            />
          </When>
          <div>
            <IndeterminateCheckbox
              withoutLabel
              readOnly
              checked={isChecked(selectedItems, value ?? "")}
              indeterminate={
                selectedSubItems.some((selectedSubItem) => {
                  const allSubValues = subCheckboxes?.map(
                    (subCheckbox) => subCheckbox.value
                  );
                  return allSubValues?.includes(selectedSubItem);
                }) && !isChecked(selectedItems, value ?? "")
              }
            />
          </div>
          <FlexContainer direction="column" alignItems="start">
            <Text align="left" mb={0} color={theme.color.text.text01}>
              {isNil(label) ? label : trimText(label, 35)}
            </Text>
            <If condition={subLabel}>
              <Then>
                <Text
                  align="left"
                  mb={0}
                  size="sm"
                  color={theme.color.text.text01}
                >
                  {subLabel}
                </Text>
              </Then>
            </If>
          </FlexContainer>
        </SidebarItemWrapperGrid>
        <When condition={expanded[index]}>
          <SubList index={index} />
        </When>
      </div>
    );
  };

  const calculateSubListHeight = (index: number) => {
    const { subCheckboxes } = filteredOptions[index];

    return (
      subCheckboxes?.reduce((acc, current) => {
        if (current.subLabel) {
          return acc + ROW_HEIGHT_WITH_SUBLABEL;
        }
        return acc + ROW_HEIGHT;
      }, 0) ?? 0
    );
  };

  const calculateExpandableRowHeight = (index: number): number => {
    if (expanded[index]) {
      if (filteredOptions[index].subLabel) {
        return ROW_HEIGHT_WITH_SUBLABEL + calculateSubListHeight(index);
      }
      return ROW_HEIGHT + calculateSubListHeight(index);
    }

    if (filteredOptions[index].subLabel) {
      return ROW_HEIGHT_WITH_SUBLABEL;
    }

    return ROW_HEIGHT;
  };

  const calculateListHeight = (): number => {
    const totalHeight = filteredOptions.reduce((sum, _, index) => {
      return sum + calculateExpandableRowHeight(index);
    }, 0);
    return Math.min(totalHeight, MAX_LIST_HEIGHT);
  };

  return (
    <>
      <div ref={filterHeaderRef}>
        <FilterHeader
          showSearchBar
          allowModeChange={allowModeChange}
          defaultMode={getSelectedMode(isExcludeSelected)}
          onSetSearch={setSearch}
          onModeChange={onModeChange}
        />
      </div>
      <FlexContainer direction="column">
        <Separator />
        <When condition={!isLoading && filteredOptions.length > 0}>
          <FlexContainer justifyContent="flex-end" css={{ width: "100%" }}>
            <TextButton
              type="button"
              css={{
                color: theme.color.interactive.link,
                "&:hover": { color: theme.color.interactive.link },
                fontSize: theme.fontSizes.sm,
                padding: theme.spacing.spacing02,
                lineHeight: "normal",
              }}
              onClick={
                areAllFilteredOptionsSelected
                  ? deselectAllFilteredOptions
                  : selectAllFilteredOptions
              }
            >
              {areAllFilteredOptionsSelected ? "Deselect All" : "Select All"}
            </TextButton>
          </FlexContainer>
        </When>
        <List
          ref={listRef}
          height={calculateListHeight()}
          itemCount={filteredOptions.length}
          itemSize={calculateExpandableRowHeight}
          width={selectableListWidth}
        >
          {ExpandableRow}
        </List>
        <When condition={isInfiniteScroll}>
          <When condition={isLoading}>
            <Loader inContent />
          </When>
          <div ref={elementRef} />
        </When>
      </FlexContainer>
    </>
  );
}

function isChecked(selectedItems: string[], value: string) {
  return selectedItems.includes(value);
}
